// Daily performance review «Απολογισμός ημέρας» and the monthly dashboard «Μηνιαία απόδοση» (handoff v08 #25, #26).
//
// RECORDED («καταγεγραμμένο») — computed from the CRM, never typed:
//   callsAttempted      every dial in call_logs that day + completed call tasks (tasks.kind 'call') of people/prospects
//                       with no dial logged that day (a dial and the task it closed count once)
//   callsCompleted      those that reached the person (call outcome in REACHED_OUTCOMES, or a call task with an outcome)
//   peopleApproached    distinct people/prospects contacted that day: dials, counted call tasks, follow-ups done with an
//                       outcome, outgoing communications, campaign activities, meetings booked
//   contactsCompleted   distinct people with a completed contact (a conversation, a reply, a booking)
//   positiveReplies     distinct people whose LAST completed contact of the day was positive (interested, call back,
//                       meeting, positive outcome, campaign reply or appointment)
//   rejections          distinct people whose last completed contact was negative (not interested, do not call, negative
//                       outcome, campaign opt-out)
//   meetingsBooked      distinct people with an appointment created that day (agenda appointment from a call or by hand —
//                       not a meeting logged afterwards as already held), a planned meeting record created that day, or a
//                       campaign «appointment»
//   followupsCompleted  follow-up tasks (tasks.kind 'followup', campaign steps included) completed that day
// SELF-ENTERED («δική μου καταχώριση»): the advisor may give their own TOTAL for a metric (work done outside the CRM).
// It is never added on top: the day counts max(recorded, own total), so logging the same call later never doubles it.
// RATES (with explicit denominators, shown "x of y"): goal completion = goals met / goals; response = positive replies /
// people approached; appointment conversion = meetings booked / completed contacts.
// RELATIONSHIP MEETINGS (owner 22 Oct 2026): coffee / referral meetings of the planner (plan_blocks.activity) are shown as
// their OWN count, next to the metrics — never added to meetingsBooked or to any rate (they are not insurance appointments).
// MONTHLY figures re-compute every day of the month from the same records (so they always match the activity) and add
// the self-entered part from the stored daily reports. Interpretation lines are deterministic rules that quote the
// numbers and their sample sizes, and never claim a cause (a campaign «coincided with» a change).
import type { Changeset, WorkspaceView } from './changeset.ts';
import { DomainError, created, opt, put, touched, type Ctx } from './cmd.ts';
import { addDays, isDay, weekday, type Day } from './dates.ts';
import { stableId } from './ids.ts';
import { isReached } from './calls.ts';
import { relationshipCounts, type RelationshipCounts } from './planner.ts';
import type { CallLog, CampaignActivity, Communication, DailyReport, Meeting, Outcome, Task } from './schema.ts';

export const REPORT_METRICS = ['callsAttempted', 'callsCompleted', 'peopleApproached', 'contactsCompleted', 'positiveReplies', 'rejections', 'meetingsBooked', 'followupsCompleted'] as const;
export type ReportMetric = typeof REPORT_METRICS[number];
export type Metrics = Record<ReportMetric, number>;
export const RATE_KEYS = ['goal_completion', 'response', 'appointment_conversion'] as const;
export type RateKey = typeof RATE_KEYS[number] | 'call_connect' | 'activity';
/** below this denominator a rate is a small sample: shown, but no change is read into it */
export const MIN_SAMPLE = 20;
export const MAX_SELF_VALUE = 9999;

const zero = (): Metrics => Object.fromEntries(REPORT_METRICS.map((k) => [k, 0])) as Metrics;
export const dailyReportId = (day: Day) => stableId('daily-report', day);
const dayOf = (iso: string | null | undefined) => (iso ?? '').slice(0, 10);

// --- the day's activity, indexed once ---------------------------------------------------------------------------
interface Bucket { calls: CallLog[]; tasksDone: Task[]; appointments: Task[]; meetings: Meeting[]; comms: Communication[]; acts: CampaignActivity[] }
export type ActivityIndex = Map<Day, Bucket>;
export function activityIndex(v: WorkspaceView): ActivityIndex {
  const m: ActivityIndex = new Map();
  const b = (d: string) => { let x = m.get(d); if (!x) { x = { calls: [], tasksDone: [], appointments: [], meetings: [], comms: [], acts: [] }; m.set(d, x); } return x; };
  for (const l of v.rows('call_logs')) if (isDay(l.day)) b(l.day).calls.push(l);
  for (const t of v.rows('tasks')) {
    if (t.status === 'done' && t.completedAt) b(dayOf(t.completedAt)).tasksDone.push(t);
    // a booking: an appointment put in the agenda that day (a meeting logged afterwards as held — origin 'flow' — is not one)
    if (t.kind === 'appointment' && t.origin !== 'flow' && t.createdAt) b(dayOf(t.createdAt)).appointments.push(t);
  }
  for (const x of v.rows('meetings')) if (x.status === 'planned' && x.createdAt) b(dayOf(x.createdAt)).meetings.push(x);
  for (const c of v.rows('communications')) if (c.at) b(dayOf(c.at)).comms.push(c);
  for (const a of v.rows('campaign_activities')) if (a.at) b(dayOf(a.at)).acts.push(a);
  return m;
}

export interface CampaignDayOutcome { campaignId: string; title: string; contacted: number; responded: number; appointment: number; opted_out: number; other: number; tasksDone: number }
export interface RecordedActivity { day: Day; metrics: Metrics; campaigns: CampaignDayOutcome[]; sources: Record<string, number> }

type Cls = 'pos' | 'neg' | 'neutral';
const fromOutcome = (o: Outcome | null | undefined): Cls | null => (o === 'positive' ? 'pos' : o === 'negative' ? 'neg' : o === 'neutral' ? 'neutral' : null);
const CALL_CLS: Record<string, Cls> = { interested: 'pos', callback: 'pos', meeting: 'pos', not_interested: 'neg', do_not_call: 'neg' };

export function recordedActivity(v: WorkspaceView, day: Day, idx: ActivityIndex = activityIndex(v)): RecordedActivity {
  const bk = idx.get(day);
  const metrics = zero();
  const sources: Record<string, number> = { call_logs: 0, call_tasks: 0, followup_tasks: 0, communications: 0, campaign_activities: 0, appointments: 0, meetings: 0 };
  if (!bk) return { day, metrics, campaigns: [], sources };
  const person = (personId?: string | null, prospectId?: string | null) => personId ?? (prospectId ? v.get('prospects', prospectId)?.convertedPersonId ?? `x:${prospectId}` : null);
  const approached = new Set<string>();
  const last = new Map<string, { at: string; cls: Cls }>();
  const reach = (k: string, at: string, cls: Cls) => { approached.add(k); const cur = last.get(k); if (!cur || at >= cur.at) last.set(k, { at, cls }); };
  // dials
  const dialled = new Set<string>();
  for (const l of bk.calls) {
    const k = person(l.personId, l.prospectId) ?? (l.phone ? `n:${l.phone}` : `log:${l.id}`);
    dialled.add(k); approached.add(k);
    metrics.callsAttempted++; sources.call_logs!++;
    if (isReached(l.outcome)) { metrics.callsCompleted++; reach(k, l.at, CALL_CLS[l.outcome] ?? 'neutral'); }
  }
  // completed tasks
  for (const t of bk.tasksDone) {
    const k = person(t.personId, t.prospectId) ?? `t:${t.id}`;
    const cls = fromOutcome(t.outcome);
    if (t.kind === 'call') {
      if (dialled.has(k)) continue; // the dial is the record of this call
      metrics.callsAttempted++; sources.call_tasks!++; approached.add(k);
      if (cls) { metrics.callsCompleted++; reach(k, t.completedAt!, cls); }
    } else if (t.kind === 'followup') {
      metrics.followupsCompleted++; sources.followup_tasks!++;
      if (cls) reach(k, t.completedAt!, cls);
    }
  }
  for (const c of bk.comms) {
    const k = person(c.personId, c.prospectId) ?? `c:${c.id}`;
    sources.communications!++;
    if (c.direction === 'out') approached.add(k);
    else reach(k, c.at, fromOutcome(c.outcome) ?? 'neutral');
  }
  // campaign outcomes
  const camp = new Map<string, CampaignDayOutcome>();
  const co = (id: string) => { let x = camp.get(id); if (!x) { x = { campaignId: id, title: v.get('campaigns', id)?.title ?? '', contacted: 0, responded: 0, appointment: 0, opted_out: 0, other: 0, tasksDone: 0 }; camp.set(id, x); } return x; };
  const booked = new Set<string>();
  for (const a of bk.acts) {
    const k = a.personId ?? `a:${a.id}`;
    sources.campaign_activities!++;
    const o = co(a.campaignId);
    if (a.type === 'contacted') { o.contacted++; approached.add(k); }
    else if (a.type === 'responded') { o.responded++; reach(k, a.at, 'pos'); }
    else if (a.type === 'appointment') { o.appointment++; reach(k, a.at, 'pos'); booked.add(k); }
    else if (a.type === 'opted_out') { o.opted_out++; reach(k, a.at, 'neg'); }
    else o.other++;
  }
  for (const t of bk.tasksDone) { const cid = (t.data as { campaignId?: string } | null | undefined)?.campaignId; if (t.origin === 'campaign' && cid) co(cid).tasksDone++; }
  // bookings
  for (const t of bk.appointments) { const k = person(t.personId, t.prospectId) ?? `t:${t.id}`; sources.appointments!++; booked.add(k); reach(k, t.createdAt, 'pos'); }
  for (const x of bk.meetings) { const k = x.personId ?? `m:${x.id}`; sources.meetings!++; booked.add(k); reach(k, x.createdAt, 'pos'); }
  metrics.peopleApproached = approached.size;
  metrics.contactsCompleted = last.size;
  metrics.positiveReplies = [...last.values()].filter((x) => x.cls === 'pos').length;
  metrics.rejections = [...last.values()].filter((x) => x.cls === 'neg').length;
  metrics.meetingsBooked = booked.size;
  return { day, metrics, campaigns: [...camp.values()].sort((a, b) => a.title.localeCompare(b.title)), sources };
}

// --- goals: read through one small interface ------------------------------------------------------------------------
/** one daily objective and how it went (the daily-goals module supplies these when it exists) */
export interface GoalOutcome { key: string; label: string; target: number; achieved: number; met: boolean }
export type GoalSource = (v: WorkspaceView, day: Day) => GoalOutcome[] | null;
let GOAL_SOURCE: GoalSource | null = null;
/** the daily-goals module (requirement 22) registers itself here; nothing else reads goals */
export function setGoalSource(fn: GoalSource | null): void { GOAL_SOURCE = fn; }
export function goalsFromSource(v: WorkspaceView, day: Day): GoalOutcome[] | null {
  try { const g = GOAL_SOURCE?.(v, day); return g && g.length ? g : null; } catch { return null; }
}
export interface GoalsSummary { source: 'goals' | 'self' | 'none'; list: GoalOutcome[]; met: number; total: number }
function goalsSummary(fromSource: GoalOutcome[] | null, stored: GoalsSummary | null | undefined, self: { met: number | null; total: number | null } | null | undefined): GoalsSummary {
  if (fromSource?.length) return { source: 'goals', list: fromSource, met: fromSource.filter((g) => g.met).length, total: fromSource.length };
  if (self && self.total != null && self.total > 0) { const total = Math.round(self.total); return { source: 'self', list: [], total, met: Math.max(0, Math.min(total, Math.round(self.met ?? 0))) }; }
  if (stored && stored.total > 0) return stored;
  return { source: 'none', list: [], met: 0, total: 0 };
}

// --- the report ---------------------------------------------------------------------------------------------------
export interface MetricLine { key: ReportMetric; recorded: number; self: number | null; total: number; extra: number; selfBelowRecorded: boolean }
export interface Rate { key: RateKey; num: number; den: number; value: number | null; small: boolean }
export const rate = (key: RateKey, num: number, den: number): Rate => ({ key, num, den, value: den > 0 ? Math.round((num / den) * 1000) / 10 : null, small: den < MIN_SAMPLE });
export interface FeedbackLine { key: string; tone: 'good' | 'info' | 'next'; params: Record<string, string | number> }
export type SelfEntries = Partial<Record<ReportMetric, number | null>>;
export interface StoredFeedback { hidden: string[]; text: string | null }
export interface DailyReportView {
  day: Day; recorded: RecordedActivity; lines: MetricLine[]; goals: GoalsSummary; rates: Rate[]; feedback: FeedbackLine[];
  selfRating: number | null; notes: string | null; stored: DailyReport | null; storedFeedback: StoredFeedback; pendingOverdue: number;
  /** relationship meetings of the day (coffee / referral) — their own count, not part of the metrics or rates */
  relationship: RelationshipCounts;
}
export const reportOf = (v: WorkspaceView, day: Day): DailyReport | null => v.get('daily_reports', dailyReportId(day)) ?? v.where('daily_reports', 'day', day)[0] ?? null;
const cleanSelf = (raw: unknown): SelfEntries => {
  const out: SelfEntries = {};
  const src = (raw ?? {}) as Record<string, unknown>;
  for (const k of REPORT_METRICS) { const n = src[k]; if (n === null || n === undefined || n === '') continue; const x = Number(n); if (Number.isFinite(x)) out[k] = Math.max(0, Math.min(MAX_SELF_VALUE, Math.round(x))); }
  return out;
};
export function metricLines(recorded: Metrics, self: SelfEntries): MetricLine[] {
  return REPORT_METRICS.map((key) => {
    const r = recorded[key], s = self[key] ?? null;
    const total = s === null ? r : Math.max(r, s);
    return { key, recorded: r, self: s, total, extra: total - r, selfBelowRecorded: s !== null && s < r };
  });
}
const totalOf = (lines: MetricLine[], k: ReportMetric) => lines.find((l) => l.key === k)!.total;
export function reportRates(lines: MetricLine[], goals: { met: number; total: number }): Rate[] {
  return [rate('goal_completion', goals.met, goals.total), rate('response', totalOf(lines, 'positiveReplies'), totalOf(lines, 'peopleApproached')), rate('appointment_conversion', totalOf(lines, 'meetingsBooked'), totalOf(lines, 'contactsCompleted'))];
}

/** constructive, deterministic feedback (no pressure words; the advisor can hide or rewrite any of it) */
export function dailyFeedback(lines: MetricLine[], goals: GoalsSummary, selfRating: number | null, pendingOverdue: number): FeedbackLine[] {
  const T = (k: ReportMetric) => totalOf(lines, k);
  const out: FeedbackLine[] = [];
  const any = lines.some((l) => l.total > 0);
  if (!any && !goals.total) out.push({ key: 'quiet_day', tone: 'info', params: {} });
  if (goals.total) {
    const r = goals.met / goals.total;
    out.push(r >= 1 ? { key: 'goals_all', tone: 'good', params: { met: goals.met, total: goals.total } }
      : r >= 0.5 ? { key: 'goals_most', tone: 'good', params: { met: goals.met, total: goals.total } }
        : goals.met > 0 ? { key: 'goals_some', tone: 'next', params: { met: goals.met, total: goals.total } }
          : { key: 'goals_none', tone: 'next', params: { total: goals.total } });
  }
  const appr = T('peopleApproached'), pos = T('positiveReplies');
  if (appr > 0 && appr < 5) out.push({ key: 'small_day', tone: 'info', params: { count: appr } });
  else if (appr >= 5 && pos / appr >= 0.3) out.push({ key: 'response_good', tone: 'good', params: { pos, appr } });
  else if (appr >= 5 && pos === 0) out.push({ key: 'response_zero', tone: 'next', params: { appr } });
  if (T('meetingsBooked') > 0) out.push({ key: 'meetings', tone: 'good', params: { count: T('meetingsBooked') } });
  if (T('followupsCompleted') > 0) out.push({ key: 'followups', tone: 'good', params: { count: T('followupsCompleted') } });
  if (pendingOverdue > 0) out.push({ key: 'pending', tone: 'next', params: { count: pendingOverdue } });
  if (selfRating !== null && selfRating <= 2) out.push({ key: 'low_rating', tone: 'info', params: { rating: selfRating } });
  if (selfRating !== null && selfRating >= 4 && any) out.push({ key: 'high_rating', tone: 'good', params: { rating: selfRating } });
  return out;
}

export interface ReportDraft { manual?: SelfEntries; selfGoals?: { met: number | null; total: number | null } | null; selfRating?: number | null; notes?: string | null }
/** the report of a day: the stored report merged with a draft being edited (draft wins) */
export function dailyReport(v: WorkspaceView, day: Day, draft: ReportDraft = {}, idx?: ActivityIndex): DailyReportView {
  const stored = reportOf(v, day);
  const recorded = recordedActivity(v, day, idx);
  const self = cleanSelf(draft.manual ?? stored?.manual);
  const lines = metricLines(recorded.metrics, self);
  const storedGoals = (stored?.goals ?? null) as GoalsSummary | null;
  const selfGoals = draft.selfGoals !== undefined ? draft.selfGoals : storedGoals?.source === 'self' ? { met: storedGoals.met, total: storedGoals.total } : null;
  const goals = goalsSummary(goalsFromSource(v, day), storedGoals, selfGoals);
  const selfRating = draft.selfRating !== undefined ? draft.selfRating : stored?.selfRating ?? null;
  const pendingOverdue = v.rows('tasks').filter((t) => t.status === 'open' && t.dueDate && t.dueDate <= day).length;
  const fb = (stored?.feedback ?? {}) as Partial<StoredFeedback>;
  return {
    day, recorded, lines, goals, rates: reportRates(lines, goals), feedback: dailyFeedback(lines, goals, selfRating, pendingOverdue),
    selfRating, notes: draft.notes !== undefined ? draft.notes : stored?.notes ?? null, stored, storedFeedback: { hidden: fb.hidden ?? [], text: fb.text ?? null }, pendingOverdue,
    relationship: relationshipCounts(v, day),
  };
}

export interface SaveReportInput { day: Day; manual?: SelfEntries; selfGoals?: { met: number | null; total: number | null } | null; selfRating?: number | null; notes?: string | null; feedback?: Partial<StoredFeedback> | null }
export function saveDailyReport(v: WorkspaceView, input: SaveReportInput, ctx: Ctx): Changeset {
  if (!isDay(input.day) || input.day > ctx.today) throw new DomainError('day_report.day');
  const r = input.selfRating ?? null;
  if (r !== null && !(Number.isInteger(r) && r >= 1 && r <= 5)) throw new DomainError('day_report.rating');
  for (const [k, n] of Object.entries(input.manual ?? {})) {
    if (!(REPORT_METRICS as readonly string[]).includes(k)) throw new DomainError('day_report.metric');
    if (n !== null && n !== undefined && !(Number.isInteger(n) && n >= 0 && n <= MAX_SELF_VALUE)) throw new DomainError('day_report.value');
  }
  const sg = input.selfGoals;
  if (sg && ((sg.total != null && !(Number.isInteger(sg.total) && sg.total >= 0 && sg.total <= 99)) || (sg.met != null && !(Number.isInteger(sg.met) && sg.met >= 0 && sg.met <= (sg.total ?? 0))))) throw new DomainError('day_report.goals');
  const view = dailyReport(v, input.day, { manual: input.manual, selfGoals: sg, selfRating: r, notes: input.notes });
  const manual = cleanSelf(input.manual);
  const fields = {
    day: input.day,
    recorded: { metrics: view.recorded.metrics, campaigns: view.recorded.campaigns, sources: view.recorded.sources, at: ctx.now },
    manual, goals: view.goals, selfRating: r, notes: opt(input.notes),
    feedback: { hidden: [...new Set(input.feedback?.hidden ?? [])].slice(0, 20), text: opt(input.feedback?.text) },
    savedAt: ctx.now, advisorId: ctx.actor || null,
  };
  const existing = reportOf(v, input.day);
  const row = existing ? touched(ctx, existing, fields) : created<'daily_reports'>(ctx, { id: dailyReportId(input.day), ...fields });
  return { action: 'day.report.save', entity: 'daily_reports', entityId: row.id, summary: input.day, puts: [put('daily_reports', row)], deletes: [] };
}

// --- the month ------------------------------------------------------------------------------------------------------
export interface MonthStats {
  month: string; from: Day; to: Day; partial: boolean; workingDays: number; activeDays: number; reports: number; selfDays: number;
  metrics: Record<ReportMetric, { recorded: number; self: number; total: number }>;
  goals: { set: number; met: number; days: number };
  campaigns: { launched: number; active: number; titles: string[]; contacted: number; responded: number; appointments: number; optedOut: number };
  rates: Rate[];
  selfRating: { avg: number | null; n: number };
  activity: Rate;
  /** relationship meetings held (coffee / referral, done) in the month — shown on their own, never in meetingsBooked */
  relationship?: { done: number; coffee: number; referral: number; people: number };
}
const monthEnd = (m: string): Day => { const [y, mo] = m.split('-').map(Number) as [number, number]; return new Date(Date.UTC(y, mo, 0)).toISOString().slice(0, 10); };
export const monthKey = (d: Day) => d.slice(0, 7);
export function previousMonth(m: string): string { const [y, mo] = m.split('-').map(Number) as [number, number]; return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`; }
export function nextMonth(m: string): string { const [y, mo] = m.split('-').map(Number) as [number, number]; return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`; }
const isWorking = (d: Day) => weekday(d) !== 0 && weekday(d) !== 6;
export function workingDaysIn(m: string, until?: Day): number { let n = 0; const end = until && until < monthEnd(m) ? until : monthEnd(m); for (let d = `${m}-01`; d <= end; d = addDays(d, 1)) if (isWorking(d)) n++; return n; }

export function monthStats(v: WorkspaceView, month: string, today: Day, idx: ActivityIndex = activityIndex(v)): MonthStats {
  const from = `${month}-01` as Day, end = monthEnd(month);
  const to = end < today ? end : today;
  const metrics = Object.fromEntries(REPORT_METRICS.map((k) => [k, { recorded: 0, self: 0, total: 0 }])) as MonthStats['metrics'];
  let activeDays = 0, workingDays = 0, reports = 0, selfDays = 0, ratingSum = 0, ratingN = 0;
  const goals = { set: 0, met: 0, days: 0 };
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (isWorking(d)) workingDays++;
    const rec = recordedActivity(v, d, idx);
    const stored = reportOf(v, d);
    const lines = metricLines(rec.metrics, cleanSelf(stored?.manual));
    if (stored) { reports++; if (stored.selfRating) { ratingSum += stored.selfRating; ratingN++; } }
    if (lines.some((l) => l.extra > 0)) selfDays++;
    for (const l of lines) { metrics[l.key].recorded += l.recorded; metrics[l.key].self += l.extra; metrics[l.key].total += l.total; }
    if (isWorking(d) && lines.some((l) => l.total > 0)) activeDays++; // activity level = active working days ÷ working days
    const g = goalsSummary(goalsFromSource(v, d), (stored?.goals ?? null) as GoalsSummary | null, null);
    if (g.total) { goals.set += g.total; goals.met += g.met; goals.days++; }
  }
  const inMonth = (iso?: string | null) => !!iso && iso.slice(0, 7) === month;
  const cs = v.rows('campaigns').filter((c) => c.launchedAt);
  const launched = cs.filter((c) => inMonth(c.launchedAt));
  const active = cs.filter((c) => c.launchedAt!.slice(0, 10) <= to && (!c.completedAt || c.completedAt.slice(0, 10) >= from));
  const acts = v.rows('campaign_activities').filter((a) => inMonth(a.at) && a.at.slice(0, 10) <= to);
  const n = (t: string) => acts.filter((a) => a.type === t).length;
  const T = (k: ReportMetric) => metrics[k].total;
  return {
    month, from, to, partial: to < end, workingDays, activeDays, reports, selfDays, metrics, goals,
    campaigns: { launched: launched.length, active: active.length, titles: active.map((c) => c.title).sort(), contacted: n('contacted'), responded: n('responded'), appointments: n('appointment'), optedOut: n('opted_out') },
    rates: [rate('goal_completion', goals.met, goals.set), rate('response', T('positiveReplies'), T('peopleApproached')), rate('appointment_conversion', T('meetingsBooked'), T('contactsCompleted')), rate('call_connect', T('callsCompleted'), T('callsAttempted'))],
    selfRating: { avg: ratingN ? Math.round((ratingSum / ratingN) * 10) / 10 : null, n: ratingN },
    activity: { ...rate('activity', activeDays, workingDays), small: workingDays < 10 },
    relationship: relationshipDone(v, from, to),
  };
}
/** the relationship meetings actually held (done) from…to, by type, with the distinct people met */
function relationshipDone(v: WorkspaceView, from: Day, to: Day): NonNullable<MonthStats['relationship']> {
  const out = { done: 0, coffee: 0, referral: 0, people: 0 };
  const people = new Set<string>();
  for (const b of v.rows('plan_blocks')) {
    if (b.day < from || b.day > to || b.status !== 'done' || (b.activity !== 'coffee' && b.activity !== 'referral_meeting')) continue;
    out.done++;
    if (b.activity === 'coffee') out.coffee++; else out.referral++;
    if (b.personId) people.add(b.personId);
  }
  out.people = people.size;
  return out;
}

export interface Line { key: string; tone: 'good' | 'watch' | 'info'; params: Record<string, string | number> }
const rateOf = (m: MonthStats, k: RateKey) => m.rates.find((r) => r.key === k)!;
const perDay = (m: MonthStats, k: ReportMetric) => (m.workingDays ? Math.round((m.metrics[k].total / m.workingDays) * 10) / 10 : 0);

/** what the numbers say — deterministic, citing the numbers and sample sizes, never a cause */
export function interpretMonth(cur: MonthStats, prev: MonthStats | null): Line[] {
  const out: Line[] = [];
  const anyActivity = REPORT_METRICS.some((k) => cur.metrics[k].total > 0) || cur.goals.set > 0;
  if (!anyActivity) return [{ key: 'no_data', tone: 'info', params: { month: cur.month } }];
  out.push({ key: cur.partial ? 'activity_partial' : 'activity', tone: 'info', params: { active: cur.activeDays, working: cur.workingDays } });
  const rising: string[] = [];
  for (const k of ['response', 'appointment_conversion', 'goal_completion'] as const) {
    const a = rateOf(cur, k), b = prev ? rateOf(prev, k) : null;
    if (!a.den) continue;
    if (a.small) { out.push({ key: 'rate_small', tone: 'info', params: { rate: k, num: a.num, den: a.den, pct: a.value ?? 0, min: MIN_SAMPLE } }); continue; }
    if (!b || !b.den || b.small) { out.push({ key: 'rate_now', tone: 'info', params: { rate: k, num: a.num, den: a.den, pct: a.value ?? 0 } }); continue; }
    const diff = Math.round(((a.value ?? 0) - (b.value ?? 0)) * 10) / 10;
    const params = { rate: k, num: a.num, den: a.den, pct: a.value ?? 0, pnum: b.num, pden: b.den, ppct: b.value ?? 0, diff: Math.abs(diff) };
    if (diff >= 5) { out.push({ key: 'rate_up', tone: 'good', params }); rising.push(k); }
    else if (diff <= -5) out.push({ key: 'rate_down', tone: 'watch', params });
    else out.push({ key: 'rate_same', tone: 'info', params });
  }
  if (prev && prev.workingDays) {
    for (const k of ['callsAttempted', 'peopleApproached', 'meetingsBooked'] as const) {
      const a = perDay(cur, k), b = perDay(prev, k);
      if (cur.metrics[k].total < 10 && prev.metrics[k].total < 10) continue;
      if (b > 0 && a >= b * 1.2) out.push({ key: 'per_day_up', tone: 'good', params: { metric: k, now: a, prev: b, total: cur.metrics[k].total, ptotal: prev.metrics[k].total } });
      else if (b > 0 && a <= b * 0.8) out.push({ key: 'per_day_down', tone: 'watch', params: { metric: k, now: a, prev: b, total: cur.metrics[k].total, ptotal: prev.metrics[k].total } });
    }
  }
  if (rising.length && cur.campaigns.active) out.push({ key: 'coincided', tone: 'info', params: { rate: rising[0]!, campaigns: cur.campaigns.titles.slice(0, 3).join(', '), count: cur.campaigns.active } });
  out.push({ key: cur.selfDays ? 'limits_self' : 'limits', tone: 'info', params: { days: cur.selfDays, reports: cur.reports } });
  return out;
}

/** realistic priorities for next month from the shortfalls (at most 3; deterministic order) */
export function monthlyPriorities(cur: MonthStats, history: MonthStats[], openOverdue: number, workingNext: number): Line[] {
  const out: Line[] = [];
  const g = rateOf(cur, 'goal_completion');
  if (g.den >= 5 && (g.value ?? 0) < 60) out.push({ key: 'fewer_goals', tone: 'watch', params: { perDay: Math.max(1, Math.round(cur.goals.met / Math.max(1, cur.goals.days))), num: g.num, den: g.den } });
  if (cur.workingDays >= 5 && cur.activeDays / cur.workingDays < 0.6) out.push({ key: 'activity_days', tone: 'watch', params: { days: Math.min(workingNext, cur.activeDays + 2), working: workingNext, active: cur.activeDays, was: cur.workingDays } });
  const r = rateOf(cur, 'response');
  if (!r.small && (r.value ?? 0) < 15) out.push({ key: 'response', tone: 'watch', params: { num: r.num, den: r.den, pct: r.value ?? 0 } });
  const c = rateOf(cur, 'appointment_conversion');
  if (!c.small && (c.value ?? 0) < 10) out.push({ key: 'conversion', tone: 'watch', params: { num: c.num, den: c.den, pct: c.value ?? 0 } });
  if (openOverdue > 0) out.push({ key: 'overdue', tone: 'watch', params: { count: openOverdue } });
  // a calls target from the advisor's own recent average (+10%), only with at least one active month
  const recent = history.filter((m) => m.metrics.callsAttempted.total > 0).slice(-3);
  if (recent.length) {
    const avg = recent.reduce((s, m) => s + m.metrics.callsAttempted.total / Math.max(1, m.workingDays), 0) / recent.length;
    out.push({ key: 'calls_target', tone: 'info', params: { perDay: Math.max(1, Math.round(avg * 1.1)), avg: Math.round(avg * 10) / 10, months: recent.length } });
  }
  if (!out.length) out.push({ key: 'keep', tone: 'good', params: {} });
  return out.slice(0, 3);
}

export interface MonthlyDashboard { months: MonthStats[]; focus: MonthStats; previous: MonthStats | null; interpretation: Line[]; priorities: Line[] }
/** the last `count` months up to the month of `focus` (default: this month) */
export function monthlyDashboard(v: WorkspaceView, today: Day, opts: { count?: number; focus?: string } = {}): MonthlyDashboard {
  const count = Math.max(2, Math.min(12, opts.count ?? 6));
  const focusKey = opts.focus && /^\d{4}-\d{2}$/.test(opts.focus) && opts.focus <= monthKey(today) ? opts.focus : monthKey(today);
  const keys: string[] = [focusKey];
  while (keys.length < count) keys.unshift(previousMonth(keys[0]!));
  const idx = activityIndex(v);
  const months = keys.map((m) => monthStats(v, m, today, idx));
  const focus = months.at(-1)!, previous = months.at(-2) ?? null;
  const overdue = v.rows('tasks').filter((t) => t.status === 'open' && t.dueDate && t.dueDate < today).length;
  return { months, focus, previous, interpretation: interpretMonth(focus, previous), priorities: monthlyPriorities(focus, months, overdue, workingDaysIn(nextMonth(focus.month))) };
}

/** CSV rows: one row per month, every metric (recorded · own entries · total) and every rate as num / den / % */
export function monthlyCsvRows(d: MonthlyDashboard, label: { metric: (k: ReportMetric) => string; rate: (k: RateKey) => string; month: string; recorded: string; self: string; total: string; activeDays: string; workingDays: string; campaigns: string; reports: string; rating: string; relationship?: string }): (string | number)[][] {
  const head: (string | number)[] = [label.month];
  for (const k of REPORT_METRICS) head.push(`${label.metric(k)} (${label.recorded})`, `${label.metric(k)} (${label.self})`, `${label.metric(k)} (${label.total})`);
  for (const r of d.focus.rates) head.push(`${label.rate(r.key)} — num`, `${label.rate(r.key)} — den`, `${label.rate(r.key)} %`);
  head.push(label.activeDays, label.workingDays, label.campaigns, label.reports, label.rating);
  if (label.relationship) head.push(label.relationship);
  const rows = d.months.map((m) => {
    const row: (string | number)[] = [m.month];
    for (const k of REPORT_METRICS) row.push(m.metrics[k].recorded, m.metrics[k].self, m.metrics[k].total);
    for (const r of m.rates) row.push(r.num, r.den, r.value ?? '');
    row.push(m.activeDays, m.workingDays, m.campaigns.active, m.reports, m.selfRating.avg ?? '');
    if (label.relationship) row.push(m.relationship?.done ?? 0);
    return row;
  });
  return [head, ...rows];
}
